/**
 * Constantes de l'édition France de Zoho Books (organisation 673863818).
 *
 * Source : `docs/SPEC_gertrude_factures_fournisseurs_edition_france.md` v1.4,
 * établie par tests API réels les 10 et 11/09/2026. Les identifiants ont été
 * recoupés le 11/09 sur l'organisation : les cinq taxes existent et sont
 * actives, et les quatre champs personnalisés figurent tels quels sur une
 * facture réelle.
 *
 * Un seul endroit pour ces valeurs. Dispersées, elles auraient divergé entre
 * les flux qui créent des factures (fournisseurs, auto-facturation Experts), et
 * une facture fausse ne se voit qu'au contrôle fiscal.
 *
 * Module PUR : aucun accès base ni réseau, pour qu'un test puisse l'importer
 * sans ouvrir de connexion.
 */

/** Base d'exigibilité de la TVA (niveau facture). Spec §4. */
export type TaxBasisType = 'accrual' | 'cash';

/** Nature de la ligne (niveau ligne). Spec §4. */
export type ProductType = 'service' | 'digital_service' | 'goods';

/**
 * Les taxes de l'organisation, par TAUX.
 *
 * La clé est le taux tel qu'il est saisi dans Gertrude (`vat_rate`). Un taux
 * absent de cette table n'a pas d'équivalent Zoho : la facture part en
 * validation manuelle plutôt que de tenter une approximation.
 */
export const TAX_IDS_PAR_TAUX: Readonly<Record<string, string>> = Object.freeze({
  '20': '1535863000006922049',
  '10': '1535863000006922059',
  '5.5': '1535863000006922069',
  '2.1': '1535863000004559001',
  '8.5': '1535863000021811359', // DOM
});

/**
 * LES TROIS TAXES À 0 %, une par motif.
 *
 * Décision du 14/09/2026. Elles remplacent les deux « conventions » VATEX
 * ci-dessous : aucun des 19 codes d'exonération connus de l'organisation ne
 * décrit la franchise en base (art. 293 B) ni l'achat à un fournisseur
 * étranger — ce sont tous des sous-articles du 261. Poser malgré tout un code
 * revenait à AFFIRMER un motif faux (« jeux de hasard et paris ») dans le champ
 * prévu pour dire la vérité.
 *
 * Une taxe à 0 % n'affirme rien : elle dit « aucune TVA sur cette ligne », et
 * son NOM porte la raison. Trois taxes distinctes plutôt qu'une seule, pour que
 * Zoho garde la distinction : le jour où l'autoliquidation sera supportée, il
 * faudra retrouver les seuls achats étrangers, et non tout ce qui porte 0 %.
 *
 * Comptablement, les trois produisent le même résultat qu'une exonération :
 * dette fournisseur = montant de la facture, zéro TVA déductible.
 */

/**
 * TVA NON DÉDUCTIBLE.
 *
 * Transport de personnes, hébergement, frais postaux : la TVA figure bien sur
 * le justificatif, mais elle est exclue du droit à déduction, et la dépense
 * s'enregistre en TTC dans le compte de charge. Or AUCUN code VATEX ne décrit
 * ce cas — la facture n'est pas exonérée, sa TVA est simplement non déductible,
 * et ce ne sont pas la même chose.
 *
 * Poser cette taxe à 0 % ne revendique donc AUCUN article du CGI, contrairement
 * à un code d'exonération : on ne déclare pas une exonération, on déclare
 * qu'aucune TVA n'est déduite sur cette ligne. Le montant reste le TTC.
 *
 * Vérifié par facture de test réelle le 14/09/2026 : Zoho l'accepte (pas de
 * 110802), le total reste le TTC, et rien n'est déduit. La spec §3 l'interdisait
 * comme doublon du mécanisme d'exonération : ce motif vaut pour les vraies
 * exonérations, pas pour celle-ci.
 *
 * L'identifiant a changé le 14/09 : la taxe « Exonéré » du premier essai a été
 * remplacée par une taxe nommée « TVA non déductible ». Les factures déjà
 * créées gardent l'ancienne, Zoho conservant la taxe historique d'une écriture.
 */
export const TAX_ID_TVA_NON_DEDUCTIBLE = '1535863000116815477';

/**
 * TVA ÉTRANGÈRE NON RÉCUPÉRABLE : le justificatif porte la TVA d'un autre pays,
 * réellement payée et encaissée là-bas (21 % espagnols sur un plein d'essence,
 * péage italien, note de restaurant belge).
 *
 * À NE PAS CONFONDRE avec l'autoliquidation ci-dessous, qui est son contraire :
 * là, le fournisseur facture HORS TAXE et c'est au preneur de liquider la TVA
 * française. Ici, l'opération a déjà été taxée sur place — un plein pris à la
 * pompe est une livraison de bien consommée en Espagne, il n'y a rien à
 * autoliquider. Les confondre ferait ressortir ces tickets le jour où Zoho
 * livrera l'autoliquidation, et déclarer une TVA qui n'est pas due.
 *
 * Cette TVA ne se déduit pas en France. Elle ne se récupère que par la demande
 * de remboursement européenne (8e directive), qui n'est pas faite pour ces
 * montants : la dépense s'enregistre donc en TTC. La taxe dédiée permet de les
 * retrouver si ces montants venaient à grossir.
 */
export const TAX_ID_TVA_ETRANGERE = '1535863000116823245';

/**
 * FRANCHISE EN BASE (art. 293 B du CGI) : fournisseur français non assujetti,
 * auto-entrepreneur ou consultant sous le seuil. Sa facture ne porte aucune TVA
 * et mentionne « TVA non applicable, art. 293 B du CGI ».
 *
 * Ce n'est PAS une exonération de l'article 261 : c'est une dispense liée au
 * chiffre d'affaires du fournisseur, qui cesse le jour où il franchit le seuil.
 * Aucun code VATEX ne la décrit dans l'organisation.
 */
export const TAX_ID_FRANCHISE_293B = '1535863000116815486';

/**
 * ACHAT À UN FOURNISSEUR ÉTRANGER sans TVA (intracommunautaire ou hors UE).
 *
 * Fiscalement, ces achats relèvent de l'AUTOLIQUIDATION : c'est au preneur de
 * collecter puis de déduire la TVA. Zoho ne la supporte pas encore (les trois
 * champs ci-dessous sont rejetés), et cette taxe ne la produit pas non plus :
 * elle NOMME les factures concernées pour qu'on les retrouve, le jour où le
 * mécanisme natif arrivera. D'ici là, le retraitement reste au comptable.
 */
export const TAX_ID_AUTOLIQUIDATION_ETRANGER = '1535863000116815490';

/**
 * Les types de dépense dont la TVA est exclue du droit à déduction.
 *
 * Ce sont exactement ceux que les règles d'extraction identifient déjà pour
 * saisir le TTC en prix unitaire (transport de personnes, hébergement, frais
 * postaux). La liste est ici plutôt qu'en dur dans le flux : elle décrit une
 * règle fiscale, pas un détail d'implémentation.
 */
export const DEPENSES_TVA_NON_DEDUCTIBLE = Object.freeze([
  'transport',
  'hebergement',
  'postal',
]);

/** La TVA de cette dépense est-elle exclue du droit à déduction ? */
export function tvaNonDeductible(typeDepense: string | null | undefined): boolean {
  return Boolean(typeDepense && DEPENSES_TVA_NON_DEDUCTIBLE.includes(typeDepense));
}

/**
 * ABANDONNÉS le 14/09/2026 : les deux « conventions » VATEX des cas B et C.
 *
 * Elles ont servi du 11 au 14/09. Ce sont des codes RÉELS de l'article 261 E
 * (jeux de hasard et paris), choisis parce qu'ils ne pouvaient entrer en
 * collision avec aucune exonération de KESTIO — mais un code d'exonération
 * AFFIRME un motif, et celui-là était faux. Les taxes à 0 % nommées ci-dessus
 * les remplacent : elles n'affirment aucun article du CGI.
 *
 * La liste reste ici pour retrouver les factures créées pendant ces trois
 * jours : ce sont les seules à corriger à la main dans Zoho.
 */
export const VATEX_CONVENTIONNELS_ABANDONNES = Object.freeze([
  'VATEX-FR-CGI261E-1', // fournisseur étranger sans TVA
  'VATEX-FR-CGI261E-2', // fournisseur français en franchise de base
]);

/** Les quatre champs personnalisés des factures fournisseurs. Spec §4. */
export const CUSTOM_FIELD_IDS = Object.freeze({
  typeDeCharge: '1535863000004332001',
  affectationPedagogique: '1535863000004326035',
  modeDePaiement: '1535863000013936001',
  banque: '1535863000036323101',
});

/**
 * Champs rejetés par l'édition France : ne JAMAIS les envoyer.
 *
 * `is_reverse_charge_applied`, `reverse_charge_vat_id` et
 * `reverse_charge_tax_id` sont refusés (« Article non valide ») alors même que
 * le message de l'erreur 110802 mentionne le reverse charge. L'autoliquidation
 * n'est pas implémentée à ce jour ; ticket Zoho ouvert (spec §1 et §7.2).
 *
 * Cette liste est là pour être lue par un humain, et par le test qui vérifie
 * qu'aucun payload ne les contient.
 */
export const CHAMPS_REVERSE_CHARGE_INTERDITS = Object.freeze([
  'is_reverse_charge_applied',
  'reverse_charge_vat_id',
  'reverse_charge_tax_id',
]);

/**
 * Ce taux est-il celui d'un AUTRE PAYS ?
 *
 * Un taux strictement positif qui ne figure pas au barème français (20, 10,
 * 5,5, 2,1, 8,5) ne peut être qu'étranger : 21 en Espagne, 22 en Italie, 19 en
 * Allemagne, 6 en Belgique. Le déduire du TAUX, et non de la devise, est ce qui
 * permet d'attraper l'Espagne et l'Italie, qui facturent en euros.
 *
 * Sert de filet : les règles d'extraction basculent normalement ces lignes en
 * TTC dès la lecture du justificatif, mais tout ce qui a été extrait avant le
 * 15/09/2026 porte encore son taux d'origine.
 */
export function tauxEtranger(taux: number | null | undefined): boolean {
  if (taux === null || taux === undefined || !Number.isFinite(taux) || taux <= 0) return false;
  return taxIdPourTaux(taux) === null;
}

/**
 * Le `tax_id` d'un taux de TVA, ou `null` si ce taux n'a pas d'équivalent.
 *
 * Rendre `null` est un refus explicite, pas un défaut : c'est à l'appelant de
 * router la facture en validation manuelle. Laisser passer une ligne sans taxe
 * déclencherait l'erreur 110802, ou pire, ferait retomber Zoho sur
 * « Exonéré de TVA » (règle absolue du §3).
 */
export function taxIdPourTaux(taux: number | null | undefined): string | null {
  if (taux === null || taux === undefined || !Number.isFinite(taux)) return null;
  // Normalisation : 5.50 et 5.5 désignent la même taxe.
  const cle = String(Math.round(taux * 100) / 100);
  return TAX_IDS_PAR_TAUX[cle] ?? null;
}
